import { Injectable, signal, computed } from '@angular/core';
import { getFirebaseBackend } from '../../authUtils';
import { User } from '../models/auth.models';
import { Observable, of, Subscription, timer } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  urlApi = environment.urlAPI;
  private _user = signal<User | null>(null);
  private _identity = signal<any | null>(null);
  private _token = signal<string | null>(null);
  private tokenWatcherSubscription?: Subscription;

  user = this._user.asReadonly();
  identity = this._identity.asReadonly();
  isAuthenticated = computed(() => !!this._token());

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    // ✅ Hidratar estado desde storage al iniciar el servicio
    this.loadSessionFromStorage();
  }

  // ✅ Cargar sesión existente al iniciar la app
  private loadSessionFromStorage(): void {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    const permissions = localStorage.getItem('permissions');

    if (token) {
      this._token.set(token);
      this.startTokenWatcher();
    }

    try {
      if (user) this._user.set(JSON.parse(user));
      if (permissions) this._identity.set(JSON.parse(permissions));
    } catch (error) {
      console.error('Error recuperando la sesión almacenada.', error);
      this.logout();
    }
  }

  // ✅ Llamar después de un login exitoso
  setSession(token: string, user: any, permissions: any): void {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('permissions', JSON.stringify(permissions));

    this._token.set(token);
    this._user.set(user);
    this._identity.set(permissions);

    this.startTokenWatcher();
  }
  // ✅ Logout limpio sin necesidad de location.reload()
  logout(): void {
    // Cancelar el temporizador.
    this.tokenWatcherSubscription?.unsubscribe();
    this.tokenWatcherSubscription = undefined;

    // Limpiar estado en memoria.
    this._user.set(null);
    this._identity.set(null);
    this._token.set(null);

    // Limpiar storage.
    localStorage.clear();
    sessionStorage.clear();

    // Navegar al login.
    this.router.navigate(['/account/login']);
  }

  /**
   * Returns the current user
   */
  public currentUser(): User {
    return getFirebaseBackend().getAuthenticatedUser();
  }

  login(user: string, password: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = {
      usr_id: user,
      usr_clave: password,
      usr_identificador: user,
    };
    return this.http.post(this.urlApi + 'AccesoUsuario/login', body, {
      headers,
      observe: 'response',
    });
  }

  register(params: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(this.urlApi + 'AccesoUsuario/Register', params, {
      headers,
      observe: 'response',
    });
  }

  validateCode(params: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(this.urlApi + 'AccesoUsuario/ValidateCode', params, {
      headers,
      observe: 'response',
    });
  }

  resendCode(params: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(this.urlApi + 'AccesoUsuario/ResendCode', params, {
      headers,
      observe: 'response',
    });
  }

  // ✅ Lee desde el signal, no directo del localStorage
  getHeaders(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        Authorization: this._token() ?? '',
      }),
    };
  }

  // ✅ Bug corregido: null en lugar de string 'undefined'
  getIdentityObs(): Observable<any> {
    return of(this._identity());
  }

  getCurrentUser(): User | null {
    return this._user();
  }

  // ✅ Firebase (si aún lo necesitas)
  public currentFirebaseUser(): User {
    return getFirebaseBackend().getAuthenticatedUser();
  }
  startTokenWatcher(): void {
    // Cancelar el temporizador anterior.
    this.tokenWatcherSubscription?.unsubscribe();
    this.tokenWatcherSubscription = undefined;

    const token = localStorage.getItem('token');

    if (!token) return;

    try {
      const jwt = token.replace(/^Bearer\s+/i, '').trim();
      const parts = jwt.split('.');

      if (parts.length !== 3) {
        this.logout();
        return;
      }

      // Decodificar el payload Base64URL del JWT.
      const base64Payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');

      const payload = JSON.parse(
        atob(base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=')),
      );

      if (typeof payload.exp !== 'number') {
        this.logout();
        return;
      }

      const expiresIn = payload.exp * 1000 - Date.now();

      if (expiresIn <= 0) {
        this.logout();
        return;
      }

      this.tokenWatcherSubscription = timer(expiresIn).subscribe(() => {
        this.logout();
      });
    } catch (error) {
      console.error('Error al leer la expiración del token.', error);
      this.logout();
    }
  }
}
